import { useState, useEffect, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { JaaSMeeting } from '@jitsi/react-sdk';
import { PhoneOff, Loader2, Video, CreditCard, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '');
const API_URL = import.meta.env.VITE_API_URL ?? '';

// ── Checkout form (montado dentro de <Elements>) ──────────────────────────────

function CheckoutForm({ amount, onPaid }: { amount: number; onPaid: (piId: string) => void }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setPaying(true);
    setError(null);

    const { paymentIntent, error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {},
      redirect: 'if_required',
    });

    setPaying(false);

    if (stripeError) {
      setError(stripeError.message ?? 'Error al procesar el pago');
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      onPaid(paymentIntent.id);
    }
  }

  const fmt = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount / 100);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-400 bg-red-900/20 border border-red-700/40 rounded-lg px-3 py-2.5">
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || paying}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors disabled:opacity-50"
      >
        {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
        {paying ? 'Procesando...' : `Pagar ${fmt}`}
      </button>
    </form>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

interface PatientToken { jwt: string; roomName: string; appId: string; }
interface JoinResponse extends Partial<PatientToken> { requiresPayment?: boolean; }

export function TeleconsultaJoinPage() {
  const { citaId } = useParams<{ citaId: string }>();

  const [phase,        setPhase]        = useState<'loading' | 'payment' | 'meeting' | 'ended' | 'error'>('loading');
  const [tokenData,    setTokenData]    = useState<PatientToken | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [amount,       setAmount]       = useState(0);
  const [errorMsg,     setErrorMsg]     = useState<string | null>(null);

  // Intenta hacer join; si recibe requiresPayment=true → pide checkout
  async function tryJoin(piId?: string) {
    setPhase('loading');
    const url = `${API_URL}/api/teleconsultas/join/${citaId}${piId ? `?pi=${piId}` : ''}`;
    try {
      const res  = await fetch(url);
      const body = await res.json() as JoinResponse;

      if (res.status === 402 || body.requiresPayment) {
        await loadCheckout();
        return;
      }

      if (!res.ok) throw new Error((body as any)?.message ?? 'Error al obtener acceso');

      setTokenData({ jwt: body.jwt!, roomName: body.roomName!, appId: body.appId! });
      setPhase('meeting');
    } catch (err: any) {
      setErrorMsg(err.message ?? 'Error al unirse a la teleconsulta');
      setPhase('error');
    }
  }

  async function loadCheckout() {
    try {
      const res  = await fetch(`${API_URL}/api/teleconsultas/checkout/${citaId}`, { method: 'POST' });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.message ?? 'Error al iniciar el pago');
      setClientSecret(body.clientSecret);
      setAmount(body.amount);
      setPhase('payment');
    } catch (err: any) {
      setErrorMsg(err.message ?? 'Error al cargar el formulario de pago');
      setPhase('error');
    }
  }

  useEffect(() => { if (citaId) tryJoin(); }, [citaId]);

  // ── Estados de UI ─────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-blue-400" />
          <p className="text-sm text-gray-400">Preparando videollamada...</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white px-6">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <PhoneOff size={24} className="text-red-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">No se pudo unir a la llamada</h2>
          <p className="text-sm text-gray-400">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === 'ended') {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <PhoneOff size={24} className="text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Consulta finalizada</h2>
          <p className="text-sm text-gray-500">La videollamada ha terminado.</p>
        </div>
      </div>
    );
  }

  if (phase === 'payment' && clientSecret) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-blue-900/40 flex items-center justify-center mx-auto mb-4">
              <CreditCard size={24} className="text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Pago de teleconsulta</h2>
            <p className="text-sm text-gray-400 mt-1">
              Completa el pago para unirte a la videollamada
            </p>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'night',
                  variables: { colorPrimary: '#3b82f6', borderRadius: '10px' },
                },
              }}
            >
              <CheckoutForm amount={amount} onPaid={(piId) => tryJoin(piId)} />
            </Elements>
          </div>
        </div>
      </div>
    );
  }

  // phase === 'meeting'
  return (
    <div className="flex flex-col h-screen bg-gray-950">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 border-b border-gray-800 flex-shrink-0">
        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        <Video size={14} className="text-white" />
        <span className="text-sm font-medium text-white">Teleconsulta</span>
      </div>

      <div className="flex-1 overflow-hidden">
        {tokenData && (
          <JaaSMeeting
            appId={tokenData.appId}
            roomName={tokenData.roomName}
            jwt={tokenData.jwt}
            configOverwrite={{
              startWithAudioMuted: false,
              startWithVideoMuted: false,
              disableThirdPartyRequests: true,
              prejoinPageEnabled: false,
            }}
            interfaceConfigOverwrite={{
              MOBILE_APP_PROMO: false,
              SHOW_JITSI_WATERMARK: false,
              SHOW_WATERMARK_FOR_GUESTS: false,
            }}
            onReadyToClose={() => setPhase('ended')}
            getIFrameRef={(iframeRef) => {
              iframeRef.style.width  = '100%';
              iframeRef.style.height = '100%';
              iframeRef.style.border = 'none';
            }}
          />
        )}
      </div>
    </div>
  );
}
